import type { SiteManifest, PublishedPage, ShareSnapshot } from "./types";
import type { SearchIndexPayload } from "./search";

const isDev =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

/**
 * Extracts the "clean" tenant subdomain from the current host. Both envs
 * share the zone root `docs.siena.cx`, and dev tenants are suffixed with
 * `-dev` (e.g. `acme-dev.docs.siena.cx`). The docs-edge Worker already
 * strips that suffix server-side before forwarding to Pages, but this
 * client helper also needs the clean name for the `/policy` API call the
 * SPA issues from the browser.
 *
 * Returns `null` when we're on the apex (`docs.siena.cx` /
 * `dev.docs.siena.cx`) because those surfaces don't belong to any tenant.
 */
function getSubdomain(): string | null {
  if (isDev) return "localhost";
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();

  // Apex hosts. Kept in sync with DOCS_APEX_HOST on the backend / Worker.
  if (host === "docs.siena.cx" || host === "dev.docs.siena.cx") return null;

  // Anchored match on the shared zone root. `sub` captures everything
  // left of `.docs.siena.cx`, including a possible `-dev` env suffix.
  const match = host.match(/^(.+)\.docs\.siena\.cx$/);
  if (!match) return null;
  let sub = match[1];

  // Strip the env suffix so the KV key (and the admin API reader policy
  // lookup) matches the stored value in both envs.
  if (sub.endsWith("-dev")) sub = sub.slice(0, -"-dev".length);
  return sub || null;
}

export class UnauthorizedError extends Error {
  constructor(message = "Unauthorized") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

async function fetchJson<T>(url: string, notFoundMessage: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (res.status === 401) {
    throw new UnauthorizedError();
  }
  if (!res.ok) throw new Error(`${notFoundMessage} (${res.status})`);
  return res.json();
}

export async function fetchManifest(): Promise<SiteManifest> {
  const url = isDev ? "/fixtures/manifest.json" : "/sites/manifest.json";
  return fetchJson<SiteManifest>(url, "Manifest not found");
}

export async function fetchPage(slug: string): Promise<PublishedPage> {
  const url = isDev
    ? `/fixtures/pages/${slug}.json`
    : `/sites/pages/${slug}.json`;
  return fetchJson<PublishedPage>(url, "Page not found");
}

export async function fetchSearchIndex(): Promise<SearchIndexPayload> {
  const url = isDev
    ? "/fixtures/search-index.json"
    : "/sites/search-index.json";
  return fetchJson<SearchIndexPayload>(url, "Search index not found");
}

export async function fetchShareSnapshot(
  token: string
): Promise<ShareSnapshot> {
  const url = isDev
    ? `/fixtures/shared/${token}.json`
    : `/sites/_shared/${token}.json`;
  return fetchJson<ShareSnapshot>(url, "Share link not found or expired");
}

export { getSubdomain };
